"use client";

import { Clock3Icon, HashIcon, PaperclipIcon, SendHorizontalIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { Button } from "../button";
import type { ComposerValue } from "../editor/composer-editor";
import { Kbd } from "../kbd";
import { EmojiPickerPopover } from "./emoji-picker";
import type { MentionItem, SlashCommand } from "./types";

export type { ComposerValue };

// The message composer (spec M2-06: topic field, mentions, `/task`). Work OS comm-rail layout in
// the DTP look: a --card box with a hairline that takes the focus ring when the editor inside has
// focus, an optional topic line above the text, the attachment strip, then a toolbar row (attach,
// the key hint, Send). The text area is BlockNote (MPL core), loaded on first render so the app
// shell never pays for ProseMirror; until it arrives the box shows the placeholder at full size.

const ComposerEditor = React.lazy(() => import("../editor/composer-editor"));

const EMPTY_MENTIONS: MentionItem[] = [];
const EMPTY_COMMANDS: SlashCommand[] = [];

export type ComposerTopic = {
  value: string;
  onChange: (value: string) => void;
  /** Existing topic names, offered while typing. */
  suggestions?: string[];
  placeholder?: string;
  /** Send waits for a topic. Off by default: an empty topic line lets the caller pick (e.g. the
   * channel's general topic), and a blank optional field must never hold the Send button. */
  required?: boolean;
};

export type ComposerProps = Omit<React.ComponentProps<"div">, "children" | "onSubmit"> & {
  onSubmit: (value: ComposerValue) => void | Promise<void>;
  placeholder?: string;
  /** Accessible name of the text field. */
  label?: string;
  mentionables?: MentionItem[];
  commands?: SlashCommand[];
  /** Zulip-style topic line above the text (channel composers). */
  topic?: ComposerTopic;
  /** `AttachmentGroup` of pending files, shown above the text. */
  attachments?: React.ReactNode;
  /** Adds the paperclip button. */
  onAttach?: () => void;
  /**
   * CHAT-FILES: files picked with the paperclip (a file dialog, several at once) or pasted from
   * the clipboard (an image copied, a file copied). Adds the paperclip when `onAttach` is absent.
   */
  onFiles?: (files: File[]) => void;
  /** Files are staged: Send works with no text (a files-only message). */
  hasFiles?: boolean;
  /** Files are still uploading: Send waits. */
  uploading?: boolean;
  /** Left of Send: e.g. an audience menu ("People only"). */
  toolbar?: React.ReactNode;
  sendLabel?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** The "↵ to send · ⇧↵ new line" hint (hidden on phones). */
  hint?: boolean;
  /**
   * CHAT-SWITCH-A: a draft that survives leaving the conversation — the blocks to start from and
   * where each change goes (null: empty or sent). Key the Composer by the draft's place so a new
   * place starts from its own.
   */
  draft?: { initial: unknown[] | null; save: (blocks: unknown[] | null) => void };
  /** CHAT-SWITCH-A: the emoji button (the full picker) in the toolbar. Default on. */
  emoji?: boolean;
  /**
   * CHAT-SWITCH-B: Slack's "Send later" (a clock left of Send). It gets the text like `onSubmit`;
   * resolving clears the editor, rejecting (the person cancelled) keeps the draft.
   */
  onSchedule?: (value: ComposerValue) => Promise<void>;
};

export function Composer({
  onSubmit,
  placeholder = "Write a message…",
  label = "Message",
  mentionables = EMPTY_MENTIONS,
  commands = EMPTY_COMMANDS,
  topic,
  attachments,
  onAttach,
  onFiles,
  hasFiles = false,
  uploading = false,
  toolbar,
  sendLabel = "Send",
  disabled = false,
  autoFocus = false,
  hint = true,
  draft,
  emoji = true,
  onSchedule,
  className,
  onPasteCapture,
  ...props
}: ComposerProps) {
  const submitRef = React.useRef<(() => void) | null>(null);
  /** The next submit is a "Send later" (the clock), not a send. */
  const laterRef = React.useRef(false);
  const insertRef = React.useRef<((text: string) => void) | null>(null);
  const fileInput = React.useRef<HTMLInputElement | null>(null);
  const [empty, setEmpty] = React.useState(true);
  const topicListId = React.useId();
  const topicMissing = topic?.required === true && topic.value.trim() === "";
  const attach = onAttach ?? (onFiles ? () => fileInput.current?.click() : undefined);
  return (
    <div
      data-slot="composer"
      // Where this posts, readable whether the lazy editor or its fallback is showing.
      data-placeholder={placeholder}
      data-disabled={disabled || undefined}
      // Unsent text is in the editor (a host can keep itself open rather than lose it).
      data-has-text={empty ? undefined : true}
      onPasteCapture={(e) => {
        onPasteCapture?.(e);
        // CHAT-FILES: a pasted image (or copied file) is attached, not inserted as text.
        const files = onFiles && !disabled ? [...(e.clipboardData?.files ?? [])] : [];
        if (files.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        onFiles?.(files);
      }}
      className={cn(
        "@container/composer flex flex-col rounded-lg border border-input bg-card shadow-sm transition-colors duration-(--dur-fast) focus-within:border-ring focus-within:outline-2 focus-within:outline-ring focus-within:outline-solid data-disabled:opacity-55",
        className,
      )}
      {...props}
    >
      {topic ? (
        <div className="flex h-9 items-center gap-2 border-b border-border-subtle px-3">
          <HashIcon aria-hidden={true} className="size-3.5 shrink-0 text-faint" />
          <input
            data-slot="composer-topic"
            aria-label="Topic"
            list={topic.suggestions?.length ? topicListId : undefined}
            value={topic.value}
            onChange={(e) => topic.onChange(e.target.value)}
            placeholder={topic.placeholder ?? "Topic"}
            disabled={disabled}
            className="h-full min-w-0 flex-1 bg-transparent text-sm font-medium text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground max-md:text-md"
          />
          {topic.suggestions?.length ? (
            <datalist id={topicListId}>
              {topic.suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          ) : null}
        </div>
      ) : null}
      {attachments ? <div className="px-3 pt-2">{attachments}</div> : null}
      <div className="max-h-[180px] min-h-10 overflow-y-auto px-3 py-2">
        <React.Suspense
          fallback={
            <p
              data-slot="composer-loading"
              aria-hidden={true}
              className="py-0.5 text-base leading-6 text-muted-foreground"
            >
              {placeholder}
            </p>
          }
        >
          <ComposerEditor
            placeholder={placeholder}
            label={label}
            mentionables={mentionables}
            commands={commands}
            disabled={disabled}
            autoFocus={autoFocus}
            submitRef={submitRef}
            insertRef={insertRef}
            initialBlocks={draft?.initial ?? null}
            {...(draft ? { onDraft: draft.save } : {})}
            onEmptyChange={setEmpty}
            allowEmpty={hasFiles && !uploading}
            onSubmit={(value) => {
              const later = laterRef.current;
              laterRef.current = false;
              // No topic yet, or files still uploading: keep the draft (a rejected send never
              // clears the editor).
              if (topicMissing || uploading)
                return Promise.reject(new Error(uploading ? "files uploading" : "topic required"));
              return later && onSchedule ? onSchedule(value) : onSubmit(value);
            }}
          />
        </React.Suspense>
      </div>
      <div className="flex h-10 items-center gap-1 px-2 pb-1">
        {attach ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Attach files"
            title="Attach files"
            onClick={attach}
            disabled={disabled}
          >
            <PaperclipIcon />
          </Button>
        ) : null}
        {emoji ? (
          <EmojiPickerPopover
            label="Emoji"
            size="icon-sm"
            side="top"
            align="start"
            disabled={disabled}
            onPick={(e) => insertRef.current?.(e)}
          />
        ) : null}
        {onFiles ? (
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            data-slot="composer-file-input"
            aria-label="Choose files"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = "";
              if (files.length) onFiles(files);
            }}
          />
        ) : null}
        {toolbar}
        {hint ? (
          // A narrow composer (a task's conversation column) drops the hint rather than wrap it.
          <span className="ml-auto flex items-center gap-1 text-xs whitespace-nowrap text-muted-foreground max-md:hidden @max-[23rem]/composer:hidden">
            <Kbd>↵</Kbd> to send · <Kbd>⇧↵</Kbd> new line
          </span>
        ) : null}
        {onSchedule ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Send later"
            title="Send later"
            className={hint ? "ml-2 max-md:ml-auto @max-[23rem]/composer:ml-auto" : "ml-auto"}
            disabled={disabled || empty || hasFiles || topicMissing || uploading}
            onClick={() => {
              laterRef.current = true;
              submitRef.current?.();
              // A submit the editor refused (empty) never reaches onSubmit: forget the mode.
              queueMicrotask(() => {
                laterRef.current = false;
              });
            }}
          >
            <Clock3Icon />
          </Button>
        ) : null}
        <Button
          size="sm"
          className={
            onSchedule
              ? "ml-1"
              : hint
                ? "ml-2 max-md:ml-auto @max-[23rem]/composer:ml-auto"
                : "ml-auto"
          }
          disabled={disabled || (empty && !hasFiles) || topicMissing || uploading}
          onClick={() => submitRef.current?.()}
        >
          <SendHorizontalIcon data-icon="inline-start" />
          {sendLabel}
        </Button>
      </div>
    </div>
  );
}
